import { useCallback } from 'react';
import { ShowSection } from 'framework/ui/layout';
import { useParams } from 'react-router-dom';
import { FormShowTemplate } from 'framework/form';
import useBillingService, {
  BillingScheduleItemResponse,
  BillingScheduleItemResult,
  BillingScheduleSensitiveItem,
  MilestoneScheduleItemResponse,
} from './billingService';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  BillingType,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import BillingList from './List/BillingList';
import { ScheduleItemStatus } from './billingTypes';
import { EditableListFormShowTemplate } from 'framework/list';

const parsePosition = (position: string): number[] => position.split('.').map(Number);

const sortByPosition = (a: BillingScheduleItemResponse, b: BillingScheduleItemResponse): number => {
  return Number(a.position) - Number(b.position);
};

const processRelatedItems = (items: BillingScheduleItemResponse[]): BillingScheduleItemResponse[] => {
  const getParentId = (item: BillingScheduleItemResponse): string | null =>
    'relatedItem' in item ? (item as MilestoneScheduleItemResponse).relatedItem || null : null;

  const parentIdsWithPendingCR = new Set(
    items
      .filter(item => getParentId(item) && item.status === ScheduleItemStatus.SYSTEM_PENDING)
      .map(item => getParentId(item)),
  );

  const itemsMap = new Map(items.map(item => [item.id, item]));
  const subPositionCounters = new Map<string, number>();

  return items.map(item => {
    const parentId = getParentId(item);
    const isLinkedCR = Boolean(parentId);

    if (isLinkedCR) {
      const parentItem = itemsMap.get(parentId!);
      if (parentItem) {
        const count = (subPositionCounters.get(parentId!) ?? 0) + 1;
        subPositionCounters.set(parentId!, count);

        return {
          ...item,
          position: `${parentItem.position}.${count}`,
          parentAlias: (parentItem as MilestoneScheduleItemResponse).alias,
          isLinkedCR: true,
        };
      }
    }

    return {
      ...item,
      hasLinkedPendingCR: parentIdsWithPendingCR.has(item.id),
    };
  });
};

const addPreviousItemInfo = (items: any[]): any[] => {
  const regularItemsSortedAsc = items
    .filter(item => !item.isLinkedCR)
    .sort((a, b) => {
      const [mainA] = parsePosition(a.position);
      const [mainB] = parsePosition(b.position);
      return mainA - mainB;
    });

  const previousStatusMap = new Map<string, ScheduleItemStatus | null>();
  regularItemsSortedAsc.forEach((item, index) => {
    const previousItem = regularItemsSortedAsc[index - 1];
    previousStatusMap.set(item.id, previousItem?.status ?? null);
  });

  return items.map(item => {
    if (item.isLinkedCR) {
      return item;
    }

    return {
      ...item,
      previousItemStatus: previousStatusMap.get(item.id) ?? null,
    };
  });
};

const mapItemWithSensitiveData = (
  sensitiveItemsMap: Map<string, BillingScheduleSensitiveItem>,
  scheduleItemsMap: Map<string, BillingScheduleItemResult>,
  billingType: BillingType,
  billingScheduleId: string,
) => {
  return (item: BillingScheduleItemResponse) => {
    const sensitiveItem = sensitiveItemsMap.get(item.id);
    const scheduleItem = scheduleItemsMap.get(item.id);

    return {
      ...item,
      billingType,
      billingScheduleId,
      amount: scheduleItem?.amount ?? null,
      currency: scheduleItem?.currency ?? null,
      hours: scheduleItem?.hours ?? item.hours ?? null,
      billingRequestId: scheduleItem?.billingRequestId ?? null,
      sensitiveData: {
        amount: sensitiveItem?.amount,
        currency: sensitiveItem?.currency,
      },
      invoiceDate: sensitiveItem?.invoiceDate ?? item.invoiceDate ?? null,
    };
  };
};

const extractBillingRequestIds = (scheduleItems: BillingScheduleItemResponse[]): string[] => {
  return [
    ...new Set(
      scheduleItems
        .map(item => ('billingRequestId' in item ? item.billingRequestId : null))
        .filter((reqId): reqId is string => Boolean(reqId)),
    ),
  ];
};

const createBillingItemsMap = (billingRequests: any[]): Map<string, BillingScheduleItemResult> => {
  return new Map(
    billingRequests.flatMap(request =>
      request.items
        .filter((item: any) => item.scheduleItemId || item.itemId)
        .map((item: any) => {
          const itemId = item.scheduleItemId || item.itemId;
          return [
            itemId,
            {
              id: itemId,
              sowId: request.sowId,
              position: '',
              invoiceDate: null,
              amount: item.amount,
              currency: item.currency,
              billingRequestId: request.id,
              hours: item.hours,
              createdAt: item.createdAt,
              createdBy: item.createdBy,
              updatedAt: item.updatedAt,
              updatedBy: item.updatedBy,
            },
          ] as [string, BillingScheduleItemResult];
        }),
    ),
  );
};

const createSensitiveItemsMap = (sensitiveResponse: any): Map<string, BillingScheduleSensitiveItem> => {
  return new Map((sensitiveResponse?.result?.[0]?.scheduleItems ?? []).map((item: any) => [item.id, item]));
};

const enrichItemWithSensitiveData = (
  sensitiveItemsMap: Map<string, BillingScheduleSensitiveItem>,
  billingItemsMap: Map<string, BillingScheduleItemResult>,
  billingType: BillingType,
  billingScheduleId: string,
) => {
  return mapItemWithSensitiveData(sensitiveItemsMap, billingItemsMap, billingType, billingScheduleId);
};

const processBillingItems = (
  scheduleItems: BillingScheduleItemResponse[],
  billingItemsMap: Map<string, BillingScheduleItemResult>,
  sensitiveItemsMap: Map<string, BillingScheduleSensitiveItem>,
  billingType: BillingType,
  billingScheduleId: string,
) => {
  const itemsWithRelatedCR = processRelatedItems(scheduleItems);
  const sortedItems = itemsWithRelatedCR.sort(sortByPosition);
  const enrichItem = enrichItemWithSensitiveData(sensitiveItemsMap, billingItemsMap, billingType, billingScheduleId);
  const itemsWithSensitiveData = sortedItems.map(enrichItem);
  const itemsWithPreviousInfo = addPreviousItemInfo(itemsWithSensitiveData);

  return itemsWithPreviousInfo;
};

const BillingContext = () => {
  const { id } = useParams();
  const { billingScheduleSearch, billingScheduleSensitiveSearch, getBillingRequest } = useBillingService();
  const { isAccessGranted } = useProjectSecurityContext();

  const getDataFunc = useCallback(async () => {
    try {
      const billingSchedule = await billingScheduleSearch({ pageSize: 100, filter: { projectId: { eq: id! } } });
      const schedule = billingSchedule?.result?.[0];
      if (!schedule) {
        return { result: [], hasBillingSchedule: false, error: false };
      }

      const { id: billingScheduleId, billingType, scheduleItems = [] } = schedule;
      const billingRequestIds = extractBillingRequestIds(scheduleItems);

      const [billingRequests, sensitiveResponse] = await Promise.all([
        billingRequestIds.length > 0
          ? Promise.all(billingRequestIds.map(reqId => getBillingRequest(billingScheduleId, reqId)))
          : Promise.resolve([]),
        billingScheduleSensitiveSearch({
          pageNumber: 0,
          pageSize: 100,
          filter: { id: { eq: billingScheduleId } },
        }).catch(() => null),
      ]);

      const billingItemsMap = createBillingItemsMap(billingRequests);
      const sensitiveItemsMap = createSensitiveItemsMap(sensitiveResponse);
      const processedItems = processBillingItems(
        scheduleItems,
        billingItemsMap,
        sensitiveItemsMap,
        billingType,
        billingScheduleId,
      );

      return { result: processedItems, billingScheduleId, hasBillingSchedule: true, error: false };
    } catch {
      return { result: [], hasBillingSchedule: false, error: true };
    }
  }, [id, billingScheduleSearch, billingScheduleSensitiveSearch, getBillingRequest]);

  return (
    <ShowSection
      sx={{ '&.section-details__wrapper': { p: '0px !important' } }}
      isVisible={isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.VIEW)}
    >
      <FormShowTemplate
        isInitialsLoadingPosition
        submitFormFunc={() => Promise.resolve()}
        afterSubmit={{
          message: 'You are awesome! Billing request has been successfully updated.',
        }}
        getDataFunc={getDataFunc}
      >
        <EditableListFormShowTemplate listSource="result">
          <BillingList />
        </EditableListFormShowTemplate>
      </FormShowTemplate>
    </ShowSection>
  );
};

export default BillingContext;
